/**
 * F-073 — Full-screen leaderboard overlay.
 *
 * Displays top-1000 with the current user's rank pinned at the bottom
 * for quick reference. Pure DOM. Receives row data via `setRows()` so
 * tests can drive it with fixtures.
 *
 * Visibility is controlled externally — `show()` / `hide()` toggle the
 * overlay. The page is mounted once on bootstrap, hidden by default.
 */

import type { AuthController, AuthUser } from "../auth/auth-controller.ts";
import { countryToFlag } from "../auth/profile.ts";
import { TIER_TABLE } from "../game/tier.ts";
import { formatNumber, t } from "../i18n/index.ts";
import type { LeaderboardRow } from "../leaderboard/online-store.ts";
import { formatDuration } from "../util/format-duration.ts";

function tierColorCss(hex: number): string {
  return `#${hex.toString(16).padStart(6, "0")}`;
}

export interface LeaderboardPage {
  element: HTMLDivElement;
  show(): void;
  hide(): void;
  setRows(rows: ReadonlyArray<LeaderboardRow>): void;
  setUserHighlight(userRow: LeaderboardRow | null): void;
  setStatus(status: "loading" | "online" | "offline" | "error"): void;
  detach(): void;
}

export interface NicknameSaveResult {
  ok: boolean;
  /** The accepted (server-side) nickname when ok=true. */
  nickname?: string;
  /** A short reason key (e.g. "empty" / "too_long") when ok=false. */
  reason?: string;
}

export interface LeaderboardPageOptions {
  onClose: () => void;
  onRefresh?: () => void;
  /** When provided, the header renders auth controls (sign-in / profile chip). */
  authController?: AuthController;
  /** Surfaced when sign-in/sign-out throw. */
  onError?: (err: Error) => void;
  /**
   * F-078 — when wired, the profile chip exposes a ✏️ edit button that
   * lets the user rename their leaderboard nickname.
   */
  onNicknameSave?: (nickname: string) => Promise<NicknameSaveResult>;
}

function rankBadgeColor(rank: number): string {
  if (rank === 1) return "#ffd84a";
  if (rank === 2) return "#cfd8e3";
  if (rank === 3) return "#dca06f";
  return "#6c7a90";
}

function buildRow(
  row: LeaderboardRow,
  isSelf: boolean,
  sticky = false,
): HTMLDivElement {
  const el = document.createElement("div");
  el.dataset["testid"] = sticky ? "leaderboard-row-self" : "leaderboard-row";
  el.dataset["userId"] = row.userId;
  // F-087 — 5-column horizontal: rank / avatar / name (1.2fr) / stats (1fr) / score.
  // mobile: name truncates first, then stats. score column stays fixed-width.
  el.className =
    "grid grid-cols-[2rem_1.75rem_minmax(0,1.2fr)_minmax(0,1fr)_5rem] items-center gap-2 rounded-md px-3 py-2 text-sm sm:grid-cols-[3rem_2.25rem_minmax(0,1.2fr)_minmax(0,1fr)_6rem] sm:gap-3 sm:px-4";
  if (isSelf) {
    el.style.background = "rgba(124, 215, 255, 0.18)";
    el.style.boxShadow = "inset 0 0 0 1px rgba(124, 215, 255, 0.5)";
  } else {
    el.style.background = "rgba(255, 255, 255, 0.04)";
  }

  const rankCell = document.createElement("div");
  rankCell.dataset["testid"] = "leaderboard-rank";
  rankCell.className = "font-display text-lg font-bold";
  rankCell.style.color = rankBadgeColor(row.rank);
  rankCell.textContent = `#${row.rank}`;

  const avatarCell = document.createElement("div");
  avatarCell.className = "flex items-center justify-center";
  // F-078 — initial-letter stub used both for "no URL" rows AND as the
  // fallback when the img fails to load.
  const stub = document.createElement("div");
  stub.className =
    "flex h-8 w-8 items-center justify-center rounded-full bg-cosmic-glow/35 font-display text-xs text-cosmic-deep";
  stub.textContent = (row.displayName.charAt(0) ?? "?").toUpperCase();

  if (row.avatarUrl) {
    const img = document.createElement("img");
    img.alt = "";
    img.referrerPolicy = "no-referrer-when-downgrade";
    img.className = "h-8 w-8 rounded-full ring-1 ring-white/20";
    img.src = row.avatarUrl;
    img.addEventListener("error", () => {
      img.remove();
      avatarCell.appendChild(stub);
    });
    avatarCell.appendChild(img);
  } else {
    avatarCell.appendChild(stub);
  }

  // Column 3 — flag + nickname (single line, truncate).
  const nameCell = document.createElement("div");
  nameCell.dataset["testid"] = "leaderboard-name";
  nameCell.className = "flex min-w-0 items-center gap-1.5 truncate text-slate-100";

  const flag = countryToFlag(row.country);
  if (flag) {
    const flagSpan = document.createElement("span");
    flagSpan.dataset["testid"] = "leaderboard-flag";
    if (row.country) flagSpan.title = row.country;
    flagSpan.className = "shrink-0 text-base leading-none";
    flagSpan.textContent = flag;
    nameCell.appendChild(flagSpan);
  }

  const nameText = document.createElement("span");
  nameText.className = "truncate";
  nameText.textContent = row.displayName;
  nameCell.appendChild(nameText);

  // Column 4 — F-087 stats column (tier disc + name · duration · merges).
  // tier-ladder-panel chip 과 동일 스타일: 색 채운 동그라미 + 작은 ring shadow.
  // F-088 — `truncate` 를 column 자체에 두면 disc 의 box-shadow / outline
  // 이 cell box 에서 잘리므로 column 은 overflow visible 유지하고 텍스트 자르기는
  // tier label 자식에 위임. 첫 자식(disc wrap) 에 좌측 여유 픽셀 확보.
  const statsCell = document.createElement("div");
  statsCell.dataset["testid"] = "leaderboard-substats";
  statsCell.className =
    "flex min-w-0 items-center gap-2 whitespace-nowrap pl-1 text-[10px] text-slate-400 sm:text-xs";

  const statsParts: HTMLElement[] = [];

  if (row.peakTier !== null && row.peakTier >= 0 && row.peakTier <= 8) {
    const tier = TIER_TABLE[row.peakTier];
    if (tier) {
      const tierWrap = document.createElement("span");
      tierWrap.dataset["testid"] = "leaderboard-tier";
      tierWrap.className = "flex shrink-0 items-center gap-1";

      const disc = document.createElement("span");
      disc.dataset["testid"] = "leaderboard-tier-disc";
      // tier-ladder-panel chip 과 일관 — 색 채운 동그라미. self 행은 outline 으로 강조.
      disc.className =
        "inline-block h-3 w-3 shrink-0 rounded-full ring-1 ring-white/25";
      disc.style.background = tierColorCss(tier.color);
      disc.style.boxShadow = `0 0 6px 0 ${tierColorCss(tier.color)}55`;
      if (isSelf) disc.style.outline = "1px solid rgba(255,255,255,0.7)";
      tierWrap.appendChild(disc);

      const tierLabel = document.createElement("span");
      tierLabel.className = "min-w-0 truncate";
      tierLabel.textContent = t(`tier.${tier.name}.name`);
      tierWrap.appendChild(tierLabel);
      statsParts.push(tierWrap);
    }
  }

  const durSpan = document.createElement("span");
  durSpan.dataset["testid"] = "leaderboard-duration";
  durSpan.className = "shrink-0";
  durSpan.textContent = formatDuration(row.durationMs);
  statsParts.push(durSpan);

  const mergesSpan = document.createElement("span");
  mergesSpan.dataset["testid"] = "leaderboard-merges";
  mergesSpan.className = "shrink-0";
  mergesSpan.textContent = t("leaderboard.mergesShort", { n: row.merges });
  statsParts.push(mergesSpan);

  // Interleave a "·" separator between parts.
  for (let i = 0; i < statsParts.length; i += 1) {
    if (i > 0) {
      const dot = document.createElement("span");
      dot.className = "shrink-0 text-slate-500";
      dot.textContent = "·";
      statsCell.appendChild(dot);
    }
    statsCell.appendChild(statsParts[i]!);
  }

  const scoreCell = document.createElement("div");
  scoreCell.dataset["testid"] = "leaderboard-score";
  scoreCell.className = "text-right font-display font-bold text-cosmic-glow";
  scoreCell.textContent = formatNumber(row.score);

  el.append(rankCell, avatarCell, nameCell, statsCell, scoreCell);
  return el;
}

export function createLeaderboardPage(
  host: HTMLElement,
  opts: LeaderboardPageOptions,
): LeaderboardPage {
  const element = document.createElement("div");
  element.dataset["testid"] = "leaderboard-page";
  element.className =
    "absolute inset-0 z-40 hidden flex-col bg-black/85 backdrop-blur-md";

  // Header.
  const header = document.createElement("div");
  header.className =
    "flex items-center justify-between border-b border-white/10 px-4 py-3 sm:px-6";

  const title = document.createElement("h2");
  title.dataset["testid"] = "leaderboard-title";
  title.className = "font-display text-lg text-cosmic-star sm:text-xl";

  const headerActions = document.createElement("div");
  headerActions.className = "flex items-center gap-2";

  // F-075 — auth chip (sign-in CTA or profile) lives in the header so
  // game screen stays clean. Only constructed when an authController
  // is provided (offline mode → omit silently).
  const authChip = document.createElement("div");
  authChip.dataset["testid"] = "leaderboard-auth";
  authChip.className = "flex items-center gap-2";

  const signInBtn = document.createElement("button");
  signInBtn.type = "button";
  signInBtn.dataset["testid"] = "leaderboard-signin";
  signInBtn.className =
    "rounded-full bg-white/95 px-3 py-1 font-display text-xs font-bold text-slate-800 transition hover:bg-white";

  const profileChip = document.createElement("div");
  profileChip.dataset["testid"] = "leaderboard-profile";
  profileChip.className =
    "flex items-center gap-2 rounded-full bg-white/10 px-2 py-1 font-display text-xs text-slate-100 ring-1 ring-white/15";

  const profileAvatar = document.createElement("img");
  profileAvatar.dataset["testid"] = "leaderboard-profile-avatar";
  profileAvatar.className = "h-6 w-6 rounded-full ring-1 ring-white/30";
  profileAvatar.alt = "";
  // F-078 — Google CDN occasionally rejects no-referrer. The default
  // policy still strips path/query so privacy stays intact.
  profileAvatar.referrerPolicy = "no-referrer-when-downgrade";

  // F-078 — fallback initial when avatar URL fails to load.
  const profileAvatarStub = document.createElement("div");
  profileAvatarStub.dataset["testid"] = "leaderboard-profile-avatar-stub";
  profileAvatarStub.className =
    "flex h-6 w-6 items-center justify-center rounded-full bg-cosmic-glow/30 font-display text-[10px] font-bold text-cosmic-deep";
  profileAvatarStub.style.display = "none";
  profileAvatar.addEventListener("error", () => {
    profileAvatar.style.display = "none";
    profileAvatarStub.style.display = "flex";
  });

  const profileName = document.createElement("span");
  profileName.dataset["testid"] = "leaderboard-profile-name";
  profileName.className = "max-w-[8rem] truncate";

  // F-078 — inline nickname editor.
  const nicknameInput = document.createElement("input");
  nicknameInput.dataset["testid"] = "leaderboard-nickname-input";
  nicknameInput.type = "text";
  nicknameInput.maxLength = 20;
  nicknameInput.className =
    "w-28 rounded-md bg-black/40 px-2 py-0.5 text-xs text-slate-100 ring-1 ring-white/15 focus:outline-none focus:ring-cosmic-glow";
  nicknameInput.style.display = "none";

  const editBtn = document.createElement("button");
  editBtn.type = "button";
  editBtn.dataset["testid"] = "leaderboard-nickname-edit";
  editBtn.className =
    "rounded-full bg-white/5 px-1.5 py-0.5 text-[10px] text-slate-300 transition hover:bg-white/15";
  editBtn.textContent = "✏️";

  const saveBtn = document.createElement("button");
  saveBtn.type = "button";
  saveBtn.dataset["testid"] = "leaderboard-nickname-save";
  saveBtn.className =
    "rounded-full bg-cosmic-glow/85 px-1.5 py-0.5 text-[10px] font-bold text-cosmic-deep transition hover:bg-cosmic-glow disabled:opacity-50";
  saveBtn.textContent = "✓";
  saveBtn.style.display = "none";

  const cancelBtn = document.createElement("button");
  cancelBtn.type = "button";
  cancelBtn.dataset["testid"] = "leaderboard-nickname-cancel";
  cancelBtn.className =
    "rounded-full bg-white/5 px-1.5 py-0.5 text-[10px] text-slate-300 transition hover:bg-white/15";
  cancelBtn.textContent = "✕";
  cancelBtn.style.display = "none";

  const editError = document.createElement("span");
  editError.dataset["testid"] = "leaderboard-nickname-error";
  editError.className = "text-[10px] text-rose-300";
  editError.style.display = "none";

  const signOutBtn = document.createElement("button");
  signOutBtn.type = "button";
  signOutBtn.dataset["testid"] = "leaderboard-signout";
  signOutBtn.className =
    "rounded-full bg-white/5 px-2 py-0.5 text-[10px] text-rose-300 transition hover:bg-rose-500/15";

  profileChip.append(
    profileAvatar,
    profileAvatarStub,
    profileName,
    nicknameInput,
    editBtn,
    saveBtn,
    cancelBtn,
    editError,
    signOutBtn,
  );
  authChip.append(signInBtn, profileChip);

  const refreshBtn = document.createElement("button");
  refreshBtn.type = "button";
  refreshBtn.dataset["testid"] = "leaderboard-refresh";
  refreshBtn.className =
    "rounded-md bg-white/5 px-3 py-1 font-display text-xs text-slate-200 transition hover:bg-white/10";

  const closeBtn = document.createElement("button");
  closeBtn.type = "button";
  closeBtn.dataset["testid"] = "leaderboard-close";
  closeBtn.className =
    "rounded-md bg-white/5 px-3 py-1 font-display text-xs text-slate-200 transition hover:bg-white/10";

  headerActions.append(authChip, refreshBtn, closeBtn);
  header.append(title, headerActions);

  // Status banner (offline/loading/error).
  const status = document.createElement("div");
  status.dataset["testid"] = "leaderboard-status";
  status.className = "px-4 py-2 text-center text-xs sm:px-6";
  status.style.display = "none";

  // List body.
  const list = document.createElement("div");
  list.dataset["testid"] = "leaderboard-list";
  list.className = "flex-1 overflow-y-auto px-3 py-3 sm:px-6";

  // Sticky self row.
  const stickyWrap = document.createElement("div");
  stickyWrap.dataset["testid"] = "leaderboard-sticky";
  stickyWrap.className =
    "border-t border-white/10 bg-black/95 px-3 py-2 sm:px-6";
  stickyWrap.style.display = "none";

  element.append(header, status, list, stickyWrap);
  host.appendChild(element);

  let cachedRows: ReadonlyArray<LeaderboardRow> = [];
  let selfUserId: string | null = null;
  let selfRow: LeaderboardRow | null = null;

  const renderTitle = (): void => {
    title.textContent = t("leaderboard.title");
    refreshBtn.textContent = t("leaderboard.refresh");
    closeBtn.textContent = t("leaderboard.close");
  };

  // F-075 — auth chip rendering. Three states:
  //   no controller   → chip hidden (offline mode)
  //   controller, not signed in → "Google 로그인" button
  //   controller, signed in     → avatar + name + "로그아웃"
  let editing = false;
  let displayedNickname = "";

  const setEditMode = (on: boolean): void => {
    editing = on;
    if (on) {
      profileName.style.display = "none";
      editBtn.style.display = "none";
      nicknameInput.style.display = "block";
      saveBtn.style.display = "block";
      cancelBtn.style.display = "block";
      nicknameInput.value = displayedNickname;
      nicknameInput.focus();
      nicknameInput.select();
    } else {
      profileName.style.display = "";
      editBtn.style.display = opts.onNicknameSave ? "block" : "none";
      nicknameInput.style.display = "none";
      saveBtn.style.display = "none";
      cancelBtn.style.display = "none";
      editError.style.display = "none";
      saveBtn.disabled = false;
      saveBtn.textContent = "✓";
    }
  };

  const renderAuth = (user: AuthUser | null): void => {
    const ctl = opts.authController;
    if (!ctl) {
      authChip.style.display = "none";
      return;
    }
    authChip.style.display = "flex";
    if (user) {
      signInBtn.style.display = "none";
      profileChip.style.display = "flex";
      displayedNickname = user.displayName;
      profileName.textContent = user.displayName;
      if (user.avatarUrl) {
        profileAvatar.src = user.avatarUrl;
        profileAvatar.style.display = "";
        profileAvatarStub.style.display = "none";
      } else {
        profileAvatar.removeAttribute("src");
        profileAvatar.style.display = "none";
        profileAvatarStub.textContent =
          (user.displayName.charAt(0) ?? "?").toUpperCase();
        profileAvatarStub.style.display = "flex";
      }
      // Edit button visible only when host wired the save callback.
      editBtn.style.display = opts.onNicknameSave && !editing ? "block" : "none";
      signOutBtn.textContent = t("auth.signout");
    } else {
      signInBtn.style.display = "block";
      signInBtn.textContent = t("auth.signin");
      profileChip.style.display = "none";
      setEditMode(false);
    }
  };

  // F-078 — wire nickname edit/save/cancel.
  editBtn.addEventListener("click", () => setEditMode(true));
  cancelBtn.addEventListener("click", () => setEditMode(false));

  const handleSave = async (): Promise<void> => {
    if (!opts.onNicknameSave || saveBtn.disabled) return;
    saveBtn.disabled = true;
    saveBtn.textContent = "…";
    editError.style.display = "none";
    try {
      const result = await opts.onNicknameSave(nicknameInput.value);
      if (result.ok) {
        displayedNickname = result.nickname ?? nicknameInput.value.trim();
        profileName.textContent = displayedNickname;
        setEditMode(false);
      } else {
        const reasonKey =
          result.reason === "empty"
            ? "profile.nicknameEmpty"
            : result.reason === "too_long"
              ? "profile.nicknameTooLong"
              : "profile.nicknameError";
        editError.textContent = t(reasonKey);
        editError.style.display = "inline";
        saveBtn.disabled = false;
        saveBtn.textContent = "✓";
      }
    } catch (err) {
      editError.textContent =
        err instanceof Error ? err.message : t("profile.nicknameError");
      editError.style.display = "inline";
      saveBtn.disabled = false;
      saveBtn.textContent = "✓";
      opts.onError?.(err as Error);
    }
  };
  saveBtn.addEventListener("click", () => {
    void handleSave();
  });
  nicknameInput.addEventListener("keydown", (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      void handleSave();
    } else if (e.key === "Escape") {
      e.preventDefault();
      setEditMode(false);
    }
  });

  const renderList = (): void => {
    list.replaceChildren();
    if (cachedRows.length === 0) {
      const empty = document.createElement("div");
      empty.dataset["testid"] = "leaderboard-empty";
      empty.className = "p-8 text-center text-sm text-slate-400";
      empty.textContent = t("leaderboard.empty");
      list.appendChild(empty);
      return;
    }
    const frag = document.createDocumentFragment();
    for (const row of cachedRows) {
      frag.appendChild(buildRow(row, row.userId === selfUserId, false));
    }
    list.appendChild(frag);
  };

  const renderSticky = (): void => {
    stickyWrap.replaceChildren();
    if (!selfRow) {
      stickyWrap.style.display = "none";
      return;
    }
    // Don't double-render if the user is already in the visible top-N
    // (they'll see their highlighted row above).
    const topHasSelf = cachedRows.some((r) => r.userId === selfRow!.userId);
    if (topHasSelf) {
      stickyWrap.style.display = "none";
      return;
    }
    stickyWrap.style.display = "block";
    const label = document.createElement("div");
    label.className = "mb-1 text-[10px] uppercase tracking-widest text-slate-500";
    label.textContent = t("leaderboard.yourRank");
    stickyWrap.append(label, buildRow(selfRow, true, true));
  };

  closeBtn.addEventListener("click", () => opts.onClose());
  refreshBtn.addEventListener("click", () => opts.onRefresh?.());

  signInBtn.addEventListener("click", () => {
    void opts.authController
      ?.signInWithGoogle(window.location.href)
      .catch((err) => opts.onError?.(err as Error));
  });
  signOutBtn.addEventListener("click", () => {
    void opts.authController
      ?.signOut()
      .catch((err) => opts.onError?.(err as Error));
  });

  let unsubAuth: (() => void) | null = null;
  if (opts.authController) {
    unsubAuth = opts.authController.subscribe(renderAuth);
  } else {
    renderAuth(null);
  }

  renderTitle();
  // Empty-state placeholder visible immediately on mount.
  renderList();

  return {
    element,
    show: () => {
      element.style.display = "flex";
      renderTitle();
    },
    hide: () => {
      element.style.display = "none";
    },
    setRows: (rows) => {
      cachedRows = rows;
      // Pull self id from the highlighted row if it's already attached.
      renderList();
      renderSticky();
    },
    setUserHighlight: (row) => {
      selfRow = row;
      selfUserId = row?.userId ?? null;
      renderList();
      renderSticky();
    },
    setStatus: (kind) => {
      if (kind === "online") {
        status.style.display = "none";
        status.textContent = "";
        return;
      }
      const map: Record<string, { text: string; color: string }> = {
        loading: {
          text: t("leaderboard.loading"),
          color: "rgba(124, 215, 255, 0.18)",
        },
        offline: {
          text: t("leaderboard.offline"),
          color: "rgba(245, 196, 83, 0.18)",
        },
        error: {
          text: t("leaderboard.error"),
          color: "rgba(255, 91, 91, 0.18)",
        },
      };
      const entry = map[kind] ?? map["offline"]!;
      status.style.display = "block";
      status.style.background = entry.color;
      status.textContent = entry.text;
    },
    detach: () => {
      unsubAuth?.();
      element.remove();
    },
  };
}
